import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MessageBubble from './MessageBubble';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ConversationView = ({ conversation, onBack, onMessageSent }) => {
  const { authFetch, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const loadMessages = useCallback(async () => {
    try {
      const res = await authFetch(`/api/messages/conversations/${conversation.id}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch, conversation.id]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Polling for new messages in this conversation
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadMessages();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [loadMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const res = await authFetch(`/api/messages/conversations/${conversation.id}/messages?content=${encodeURIComponent(newMessage.trim())}`, {
        method: 'POST'
      });

      if (res.ok) {
        setNewMessage('');
        loadMessages();
        if (onMessageSent) onMessageSent();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to send message');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const otherUser = conversation.other_user || {};
  let profileImageUrl = otherUser.profile_image;
  if (profileImageUrl && !profileImageUrl.startsWith('http')) {
    profileImageUrl = `${API_BASE_URL}${profileImageUrl}`;
  }

  return (
    <div className="messages-page">
      <div className="messages-container conversation-view">
        <div className="conversation-header">
          <button className="back-button" onClick={onBack}>
            &larr; Back
          </button>
          <div className={`conversation-header__avatar ${profileImageUrl ? 'has-image' : ''}`}>
            {profileImageUrl ? (
              <img src={profileImageUrl} alt={otherUser.name || 'User'} />
            ) : (
              (otherUser.name || otherUser.email || '?').charAt(0).toUpperCase()
            )}
          </div>
          <div className="conversation-header__info">
            <span className="conversation-header__name">
              {otherUser.name || otherUser.email || 'Unknown User'}
            </span>
          </div>
        </div>

        <div className="messages-thread">
          {loading ? (
            <div className="messages-loading">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="messages-empty-thread">
              <p>No messages yet. Say hello!</p>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  isOwn={message.sender_id === user?.id}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <form className="message-input-form" onSubmit={handleSend}>
          <input
            ref={inputRef}
            type="text"
            className="message-input"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            maxLength={2000}
            disabled={sending}
          />
          <button
            type="submit"
            className="btn-send"
            disabled={!newMessage.trim() || sending}
          >
            {sending ? '...' : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ConversationView;
